import React, { useEffect, useRef, useState, useCallback } from "react"
import { Spin, Button } from "antd"
import { Icon } from "components"
import Strings from "utils/strings"
import { FleetTrackingMapProps, SelectedVehicleInfo } from "./types"
import { fleetAuthTokenFetcher, clearTokenCache } from "utils/fleetAuth"
import {
	FLEET_MAP_COLORS,
	takenPolylineCustomization,
	activePolylineCustomization,
	getStopMarkerColor,
} from "utils/fleetMapConfig"
import { createInfoWindowContent, isSameDay } from "./utils"
import {
	getDefaultStyle,
	buttonStyle,
	loadingOverlayStyle,
	errorContainerStyle,
	poiButtonContainerStyle,
	legendContainerStyle,
	legendItemStyle,
	legendSectionTitleStyle,
	mapStyles,
} from "./styles"
import VehicleInfoPanel from "./components/VehicleInfoPanel"

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
const FLEET_ENGINE_PROJECT_ID = process.env.REACT_APP_FLEET_ENGINE_PROJECT_ID

declare const google: any

const FleetTrackingMap: React.FC<FleetTrackingMapProps> = ({
	deliveryVehicleId,
	addresses,
	routeDate,
	mapContainerStyle,
	onVehicleUpdate,
	isFullscreen = false,
	alreadyTraveledPath,
}) => {
	const mapContainerRef = useRef<HTMLDivElement>(null)
	const mapViewRef = useRef<any>(null)
	const locationProviderRef = useRef<any>(null)
	const infoWindowRef = useRef<any>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [isMapReady, setIsMapReady] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [showPOIs, setShowPOIs] = useState(false)
	const [selectedVehicle, setSelectedVehicle] =
		useState<SelectedVehicleInfo | null>(null)
	const isInitializedRef = useRef(false)
	const alreadyTraveledPolylineRef = useRef<any>(null)

	const handleTogglePOIs = useCallback(() => {
		if (!mapViewRef.current?.map) return

		const newValue = !showPOIs
		setShowPOIs(newValue)

		mapViewRef.current.map.setOptions({
			styles: newValue ? [] : mapStyles.hidePOIs,
			clickableIcons: newValue,
		})
	}, [showPOIs])

	const loadJourneySharingLibrary = useCallback(async (): Promise<void> => {
		if (
			typeof google !== "undefined" &&
			google.maps?.journeySharing?.FleetEngineDeliveryVehicleLocationProvider
		) {
			return
		}

		if (typeof google !== "undefined" && google.maps?.importLibrary) {
			await google.maps.importLibrary("journeySharing")
			return
		}

		return new Promise((resolve, reject) => {
			const existingScript = document.querySelector(
				'script[src*="maps.googleapis.com"]'
			)
			if (existingScript) {
				const checkLoaded = setInterval(() => {
					if (typeof google !== "undefined" && google.maps?.importLibrary) {
						clearInterval(checkLoaded)
						google.maps
							.importLibrary("journeySharing")
							.then(resolve)
							.catch(reject)
					}
				}, 100)
				setTimeout(() => {
					clearInterval(checkLoaded)
					reject(new Error("Timeout waiting for Google Maps"))
				}, 10000)
				return
			}

			const script = document.createElement("script")
			script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=journeySharing`
			script.async = true
			script.defer = true

			script.onload = () => {
				if (
					google.maps?.journeySharing
						?.FleetEngineDeliveryVehicleLocationProvider
				) {
					resolve()
				} else {
					reject(new Error("Journey Sharing library not available"))
				}
			}

			script.onerror = () =>
				reject(new Error("Failed to load Google Maps script"))

			document.head.appendChild(script)
		})
	}, [])

	useEffect(() => {
		const initializeFleetTracking = async () => {
			if (
				!mapContainerRef.current ||
				!deliveryVehicleId ||
				isInitializedRef.current
			) {
				return
			}

			try {
				setIsLoading(true)
				setError(null)

				await loadJourneySharingLibrary()

				const routeDateObj = new Date(routeDate)
				const startOfDay = new Date(routeDateObj)
				startOfDay.setHours(0, 0, 0, 0)
				const endOfDay = new Date(routeDateObj)
				endOfDay.setHours(23, 59, 59, 999)

				const locationProvider =
					new google.maps.journeySharing.FleetEngineDeliveryVehicleLocationProvider(
						{
							projectId: FLEET_ENGINE_PROJECT_ID,
							authTokenFetcher: fleetAuthTokenFetcher,
							deliveryVehicleId,
							shouldShowTasks: true,
							shouldShowOutcomeLocations: true,
							taskFilterOptions: {
								completionTimeFrom: startOfDay,
								completionTimeTo: endOfDay,
							},
							takenPolylineCustomization,
							activePolylineCustomization,
							deliveryVehicleMarkerCustomization: (params: any) => {
								params.marker.addListener("click", () => {
									if (infoWindowRef.current) {
										infoWindowRef.current.close()
									}

									const vehicle = params.vehicle
									setSelectedVehicle({
										licensePlate: vehicle?.attributes?.licensePlate || "N/A",
										speed:
											vehicle?.latestVehicleLocationUpdate
												?.speedKilometersPerHour != null
												? Math.round(
														vehicle.latestVehicleLocationUpdate
															.speedKilometersPerHour
													)
												: null,
										lastUpdate: vehicle?.latestVehicleLocationUpdate?.time
											? new Date(vehicle.latestVehicleLocationUpdate.time)
											: null,
										remainingStops:
											vehicle?.remainingVehicleJourneySegments?.length || 0,
										heading:
											vehicle?.latestVehicleLocationUpdate?.heading ?? null,
										remainingDistance: vehicle?.remainingDistanceMeters ?? null,
									})
								})
							},
							taskMarkerCustomization: (params: any) => {
								if (params.task?.targetTimeWindow?.startTime) {
									const taskDate = new Date(
										params.task.targetTimeWindow.startTime
									)
									if (!isSameDay(taskDate, routeDate)) {
										params.marker.setVisible(false)
										return
									}
								}

								params.marker.addListener("click", () => {
									if (infoWindowRef.current) {
										infoWindowRef.current.close()
									}

									const trackingId = params.task?.trackingId || params.task?.id
									const infoWindow = new google.maps.InfoWindow({
										content: createInfoWindowContent(
											trackingId ? [trackingId] : []
										),
									})

									infoWindowRef.current = infoWindow
									infoWindow.open(mapViewRef.current?.map, params.marker)
								})
							},

							plannedStopMarkerCustomization: (params: any) => {
								const segment =
									params.vehicle?.remainingVehicleJourneySegments?.[
										params.stopIndex
									]
								const tasks = segment?.stop?.tasks || []

								const state = segment?.stop?.state

								if (state === "ENROUTE" || state === "ARRIVED") {
									const color = getStopMarkerColor(state)!
									const currentIcon = params.marker.getIcon()
									params.marker.setIcon({
										...(typeof currentIcon === "object" ? currentIcon : {}),
										fillColor: color,
										fillOpacity: 1,
									})
								}
								if (tasks[0]?.targetTimeWindow?.startTime) {
									const taskDate = new Date(tasks[0].targetTimeWindow.startTime)
									if (!isSameDay(taskDate, routeDate)) {
										params.marker.setVisible(false)
										return
									}
								}

								params.marker.addListener("click", () => {
									if (infoWindowRef.current) {
										infoWindowRef.current.close()
									}

									let orderIds: string[] = []
									tasks.forEach((task: any) => {
										const matchedAddress = addresses.find(
											(address) => address.order?.fleetTaskId === task.id
										)
										if (matchedAddress?.order?.orderId) {
											orderIds.push(matchedAddress.order.orderId)
										}

										if (matchedAddress?.order?.bundledOrders) {
											matchedAddress.order.bundledOrders.forEach(
												(bundledOrder: any) => {
													if (
														bundledOrder.fleetTaskId === task.id &&
														bundledOrder.orderId
													) {
														orderIds.push(bundledOrder.orderId)
													}
												}
											)
										}
									})

									const infoWindow = new google.maps.InfoWindow({
										content: createInfoWindowContent(orderIds),
									})

									infoWindowRef.current = infoWindow
									infoWindow.open(mapViewRef.current?.map, params.marker)
								})
							},
							taskOutcomeMarkerCustomization: (params: any) => {
								params.marker.addListener("click", () => {
									if (infoWindowRef.current) {
										infoWindowRef.current.close()
									}
									let orderIds: string[] = []
									orderIds.push(params.task.trackingId)

									const infoWindow = new google.maps.InfoWindow({
										content: createInfoWindowContent(orderIds),
									})

									infoWindowRef.current = infoWindow
									infoWindow.open(mapViewRef.current?.map, params.marker)
								})
							},
						}
					)

				locationProviderRef.current = locationProvider

				locationProvider.addListener("update", (e: any) => {
					if (e.deliveryVehicle && onVehicleUpdate) {
						onVehicleUpdate(e.deliveryVehicle)
					}
				})

				locationProvider.addListener("error", (e: any) => {
					console.error("Fleet Engine location provider error:", e)
					clearTokenCache()
				})

				const mapView = new google.maps.journeySharing.JourneySharingMapView({
					element: mapContainerRef.current,
					locationProviders: [locationProvider],
					mapOptions: {
						center: { lat: 38.72152, lng: -9.142161 },
						zoom: 12,
						mapTypeControl: true,
						fullscreenControl: false,
						streetViewControl: false,
						clickableIcons: false,
						styles: mapStyles.hidePOIs,
					},
				})

				mapViewRef.current = mapView
				isInitializedRef.current = true

				setIsLoading(false)
				setIsMapReady(true)
			} catch (err) {
				console.error("Fleet tracking initialization error:", err)
				setError(
					err instanceof Error
						? err.message
						: Strings.routes.fleetTracking.errorInitializing
				)
				setIsLoading(false)
			}
		}

		initializeFleetTracking()

		return () => {
			if (infoWindowRef.current) {
				infoWindowRef.current.close()
				infoWindowRef.current = null
			}
			if (alreadyTraveledPolylineRef.current) {
				alreadyTraveledPolylineRef.current.setMap(null)
				alreadyTraveledPolylineRef.current = null
			}
			if (locationProviderRef.current) {
				locationProviderRef.current.deliveryVehicleId = ""
				locationProviderRef.current = null
			}
			mapViewRef.current = null
			isInitializedRef.current = false
			setIsMapReady(false)
			setSelectedVehicle(null)
			if (mapContainerRef.current) {
				mapContainerRef.current.innerHTML = ""
			}
		}
	}, [
		deliveryVehicleId,
		loadJourneySharingLibrary,
		onVehicleUpdate,
		routeDate,
		addresses,
	])

	useEffect(() => {
		if (!isMapReady || !mapViewRef.current?.map) return

		if (alreadyTraveledPolylineRef.current) {
			alreadyTraveledPolylineRef.current.setMap(null)
			alreadyTraveledPolylineRef.current = null
		}

		if (!alreadyTraveledPath?.length) return

		alreadyTraveledPolylineRef.current = new google.maps.Polyline({
			path: alreadyTraveledPath,
			strokeColor: FLEET_MAP_COLORS.routeTaken,
			strokeOpacity: 0.8,
			strokeWeight: 6,
			map: mapViewRef.current.map,
		})
	}, [alreadyTraveledPath, isMapReady])

	const defaultStyle = getDefaultStyle(isFullscreen)

	if (error) {
		return (
			<div
				style={{
					...defaultStyle,
					...mapContainerStyle,
					...errorContainerStyle,
				}}
			>
				{error}
			</div>
		)
	}

	const markerLegendItems = [
		{
			color: FLEET_MAP_COLORS.markerSuccess,
			label: Strings.routes.fleetTracking.legendSuccess,
		},
		{
			color: FLEET_MAP_COLORS.markerFailed,
			label: Strings.routes.fleetTracking.legendFailed,
		},
		{
			color: FLEET_MAP_COLORS.markerInTransit,
			label: Strings.routes.fleetTracking.legendInTransit,
		},
		{
			color: FLEET_MAP_COLORS.markerEnroute,
			label: Strings.routes.fleetTracking.legendEnroute,
		},
		{
			color: FLEET_MAP_COLORS.markerArrived,
			label: Strings.routes.fleetTracking.legendArrived,
		},
		{
			color: FLEET_MAP_COLORS.markerOutcome,
			label: Strings.routes.fleetTracking.legendOutcomeLocation,
		},
	]

	const routeLegendItems = [
		{
			color: FLEET_MAP_COLORS.routeTaken,
			label: Strings.routes.fleetTracking.legendRouteCompleted,
		},
		{
			color: FLEET_MAP_COLORS.routeActive,
			label: Strings.routes.fleetTracking.legendRouteActive,
		},
		{
			color: FLEET_MAP_COLORS.routeRemaining,
			label: Strings.routes.fleetTracking.legendRouteRemaining,
		},
	]

	return (
		<>
			<div
				style={{
					position: "relative",
					...defaultStyle,
					...mapContainerStyle,
				}}
			>
				{isLoading && (
					<div style={loadingOverlayStyle}>
						<Spin size="large" />
					</div>
				)}
				<div
					ref={mapContainerRef}
					style={{
						width: "100%",
						height: "100%",
						borderRadius: isFullscreen ? 0 : 5,
					}}
				/>
				{selectedVehicle && (
					<VehicleInfoPanel
						vehicle={selectedVehicle}
						onClose={() => setSelectedVehicle(null)}
					/>
				)}
				<div style={poiButtonContainerStyle}>
					<Button
						size="small"
						onClick={handleTogglePOIs}
						style={{
							...buttonStyle,
							backgroundColor: showPOIs ? "#1890ff" : "white",
							color: showPOIs ? "white" : "rgba(0, 0, 0, 0.87)",
						}}
					>
						<Icon
							name={showPOIs ? "eye" : "eye-outline"}
							style={{
								fontSize: 14,
								color: showPOIs ? "white" : "rgba(0, 0, 0, 0.6)",
							}}
						/>
						<span>POIs</span>
					</Button>
				</div>
			</div>
			<div style={legendContainerStyle}>
				<div>
					<span style={legendSectionTitleStyle}>
						{Strings.routes.fleetTracking.legendMarkers}
					</span>
					<div
						style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 4 }}
					>
						{markerLegendItems.map((item) => (
							<div key={item.color} style={legendItemStyle}>
								<span
									style={{
										width: 10,
										height: 10,
										borderRadius: "50%",
										backgroundColor: item.color,
										display: "inline-block",
									}}
								/>
								<span>{item.label}</span>
							</div>
						))}
					</div>
				</div>
				<div>
					<span style={legendSectionTitleStyle}>
						{Strings.routes.fleetTracking.legendRoutes}
					</span>
					<div
						style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 4 }}
					>
						{routeLegendItems.map((item) => (
							<div key={item.color} style={legendItemStyle}>
								<span
									style={{
										width: 20,
										height: 4,
										borderRadius: 2,
										backgroundColor: item.color,
										display: "inline-block",
									}}
								/>
								<span>{item.label}</span>
							</div>
						))}
					</div>
				</div>
			</div>
		</>
	)
}

export default FleetTrackingMap
